import { useMemo } from 'react'
import isEmpty from 'lodash.isempty'
import { getPersonActionsHistory } from '../utils'

/**
 * Custom hook to manage history filtering and field processing
 * @param {Object} currentImage - The current image object
 * @param {Object} selectedPerson - Currently selected person
 * @param {Object} fieldFilter - Current filter configuration
 * @param {boolean} loading - Whether data is loading
 * @param {string} selectedPersonId - Selected person ID
 * @param {string} personName - Person name for display
 * @param {Function} t - Translation function
 * @returns {Object} Filtered history data and filter title
 */
export const useHistoryFilters = (
  currentImage,
  selectedPerson,
  fieldFilter,
  loading,
  selectedPersonId,
  personName,
  t
) => {
  // Get person actions history (memoized)
  const personActionsHistory = useMemo(() => {
    if (!currentImage?.actions || !selectedPerson) return []
    return getPersonActionsHistory({
      actions: currentImage.actions,
      person: selectedPerson,
    })
  }, [currentImage?.actions, selectedPerson])

  // Filter the history based on current filter
  const filteredHistoryFields = useMemo(() => {
    if (loading || !selectedPersonId || isEmpty(fieldFilter)) {
      return []
    }

    const unfilteredActions = personActionsHistory
    const primaryFilter = fieldFilter.value?.primary
    const includeAllValues = primaryFilter === 'ALL_CHANGES'

    if (includeAllValues) {
      return unfilteredActions
    }

    const filteredActions = []

    // Filter by field types
    unfilteredActions.forEach((field) => {
      if (field.typeData?.fieldType === primaryFilter) {
        filteredActions.push(field)
      }
    })

    // Filter by relationship type
    unfilteredActions.forEach((field) => {
      if (field.typeData?.relKey === primaryFilter) {
        filteredActions.push(field)
      }
    })

    // Handle secondary and tertiary filters for complex filters like Birthplace
    const secondaryFilter = fieldFilter.value?.secondary
    const tertiaryFilter = fieldFilter.value?.tertiary

    if (primaryFilter === 'EVENT') {
      unfilteredActions
        .filter((event) => !secondaryFilter || event.eventType === secondaryFilter)
        .forEach((event) => {
          const hasMatchingSubElements = event?.subElements?.filter(
            (item) => !tertiaryFilter || item.elementType === tertiaryFilter
          )
          if (hasMatchingSubElements?.length > 0) {
            filteredActions.push(event)
          }
        })
    }

    return filteredActions
  }, [loading, selectedPersonId, fieldFilter, personActionsHistory])

  // Get title based on filter
  const filterTitle = useMemo(() => {
    if (!fieldFilter || fieldFilter.value?.primary === 'ALL_CHANGES') {
      return t('history-viewer.heading', 'All Changes for Image')
    }

    return t('history-viewer.list.title', '{fieldType} changes for {personName}', {
      fieldType: fieldFilter.label,
      personName,
    })
  }, [fieldFilter, personName, t])

  return {
    filteredHistoryFields,
    filterTitle,
  }
}
